import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	reject,
} from "../types";

/**
 * A curated set of three-letter words a nine-year-old already reads. Short on
 * purpose: a ladder is only fun when every rung is a word you know, and a big
 * dictionary fills the choices with things like "gib" and "vug".
 *
 * Nothing here is typed — the words one letter away are offered as buttons — so
 * this list is the entire vocabulary of the game.
 *
 * Which makes the list itself the thing to get right, and it was not. A player
 * chooses a word and it appears on the other child's screen, up to thirty times
 * a game, and the graph is dense enough to walk almost anywhere in four steps.
 * So the unkind words are gone, and so are `gun` and `war`, which belong in
 * nobody's list for nine-year-olds whatever the graph does with them.
 */
export const RUNG_WORDS = [
	"bad",
	"bag",
	"ban",
	"bat",
	"bed",
	"bee",
	"beg",
	"bet",
	"big",
	"bin",
	"bit",
	"bog",
	"bow",
	"box",
	"boy",
	"bug",
	"bun",
	"bus",
	"but",
	"can",
	"cap",
	"car",
	"cat",
	"cod",
	"cog",
	"cop",
	"cot",
	"cub",
	"cup",
	"cut",
	"dad",
	"dam",
	"day",
	"den",
	"dig",
	"dip",
	"dog",
	"dot",
	"dug",
	"ear",
	"eat",
	"egg",
	"end",
	"fan",
	"far",
	"fed",
	"fig",
	"fin",
	"fit",
	"fix",
	"fog",
	"fox",
	"fun",
	"fur",
	"gap",
	"get",
	"got",
	"gum",
	"hat",
	"hay",
	"hen",
	"hid",
	"him",
	"hip",
	"hop",
	"hot",
	"hug",
	"hut",
	"jam",
	"jar",
	"jet",
	"job",
	"jog",
	"jug",
	"keg",
	"key",
	"kid",
	"kit",
	"lad",
	"lag",
	"lap",
	"law",
	"lay",
	"leg",
	"let",
	"lid",
	"lip",
	"lit",
	"log",
	"lot",
	"low",
	"man",
	"map",
	"mat",
	"men",
	"met",
	"mop",
	"mud",
	"mug",
	"nap",
	"net",
	"nod",
	"not",
	"now",
	"nut",
	"oak",
	"odd",
	"oil",
	"old",
	"owl",
	"pad",
	"pan",
	"pat",
	"paw",
	"pay",
	"peg",
	"pen",
	"pet",
	"pin",
	"pit",
	"pod",
	"pop",
	"pot",
	"pup",
	"put",
	"rag",
	"ram",
	"ran",
	"raw",
	"ray",
	"red",
	"rib",
	"rid",
	"rig",
	"rim",
	"rip",
	"rod",
	"rot",
	"row",
	"rub",
	"rug",
	"run",
	"sag",
	"sap",
	"sat",
	"saw",
	"say",
	"sea",
	"see",
	"set",
	"sit",
	"six",
	"sky",
	"son",
	"sub",
	"sum",
	"sun",
	"tag",
	"tan",
	"tap",
	"tar",
	"tax",
	"tea",
	"ten",
	"tie",
	"tin",
	"tip",
	"toe",
	"ton",
	"top",
	"tow",
	"toy",
	"tub",
	"tug",
	"van",
	"vet",
	"wax",
	"way",
	"web",
	"wed",
	"wet",
	"wig",
	"win",
	"wit",
	"wok",
	"won",
	"yak",
	"yam",
	"yes",
	"yet",
	"zip",
	"zoo",
] as const;

/** Longest a game can run before it is settled on who got closest. */
export const RUNG_MOVE_LIMIT = 30;
/** How far apart a puzzle's two ends should be. */
const MIN_RUNGS = 3;
const MAX_RUNGS = 5;

function oneLetterApart(a: string, b: string): boolean {
	let differences = 0;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i] && ++differences > 1) return false;
	}
	return differences === 1;
}

/**
 * Built once, at load. Every `legalMoves` call reads it, and recomputing a
 * couple of hundred squared comparisons each time would be silly.
 */
const NEIGHBOURS: ReadonlyMap<string, readonly string[]> = (() => {
	const map = new Map<string, string[]>();
	for (const word of RUNG_WORDS) map.set(word, []);
	for (let i = 0; i < RUNG_WORDS.length; i++) {
		for (let j = i + 1; j < RUNG_WORDS.length; j++) {
			const a = RUNG_WORDS[i];
			const b = RUNG_WORDS[j];
			if (oneLetterApart(a, b)) {
				map.get(a)?.push(b);
				map.get(b)?.push(a);
			}
		}
	}
	return map;
})();

export function laddersFrom(word: string): readonly string[] {
	return NEIGHBOURS.get(word) ?? [];
}

/** Steps from `word` to every word it can reach, by breadth-first search. */
export function distancesFrom(word: string): ReadonlyMap<string, number> {
	const seen = new Map<string, number>([[word, 0]]);
	const queue = [word];
	for (let head = 0; head < queue.length; head++) {
		const current = queue[head];
		const depth = seen.get(current) ?? 0;
		for (const next of laddersFrom(current)) {
			if (seen.has(next)) continue;
			seen.set(next, depth + 1);
			queue.push(next);
		}
	}
	return seen;
}

/**
 * Picks a start and a target a few rungs apart.
 *
 * Choosing the target from a search *outwards from the start* is what makes
 * every puzzle solvable by construction — there is no hand-checked list of
 * pairs that a later edit to the word list could quietly break.
 */
function makePuzzle(rng: Rng): { start: string; target: string } {
	const words = rng.shuffle(RUNG_WORDS);
	for (const start of words) {
		const reachable = [...distancesFrom(start)].filter(
			([, steps]) => steps >= MIN_RUNGS && steps <= MAX_RUNGS,
		);
		if (reachable.length === 0) continue;
		return { start, target: reachable[rng.int(reachable.length)][0] };
	}
	// Unreachable in practice; the list is far too connected for it.
	return { start: "cat", target: "dog" };
}

export interface WordLadderState extends BaseState {
	kind: "wordladder";
	start: string;
	target: string;
	/** Where each player is standing now. */
	at: [string, string];
	/** Every rung each player has stepped on, in order. */
	paths: [string[], string[]];
	movesMade: number;
}

export interface WordLadderMove {
	word: string;
}

/** Whoever is nearer the target wins; equal distance is a draw. */
function settleOnCloseness(state: WordLadderState): WordLadderState {
	const distances = distancesFrom(state.target);
	const far = RUNG_WORDS.length;
	const mine = distances.get(state.at[0]) ?? far;
	const theirs = distances.get(state.at[1]) ?? far;
	const winner = mine === theirs ? null : mine < theirs ? 0 : 1;
	return { ...state, turn: null, result: { winner } };
}

export const wordLadder: GameDefinition<WordLadderState, WordLadderMove> = {
	id: "wordladder",
	category: "education",
	title: "Word Ladder Race",
	emoji: "🪜",
	blurb: "Change one letter at a time to climb from one word to the other.",
	howTo: [
		"You both start on the same word and race to the target word.",
		"On your turn, tap one of the words that is one letter different.",
		"Reach the target in fewer steps to win — the same steps is a tie.",
	],

	create(rng) {
		const { start, target } = makePuzzle(rng);
		return {
			kind: "wordladder",
			start,
			target,
			at: [start, start],
			paths: [[start], [start]],
			movesMade: 0,
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const word = (raw as { word?: unknown }).word;
		if (typeof word !== "string") return null;
		return NEIGHBOURS.has(word) ? { word } : null;
	},

	apply(state, player, move): MoveOutcome<WordLadderState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (!laddersFrom(state.at[player]).includes(move.word)) {
			return reject("That word is more than one letter away.");
		}

		const at: [string, string] = [...state.at];
		at[player] = move.word;
		const paths: [string[], string[]] = [[...state.paths[0]], [...state.paths[1]]];
		paths[player].push(move.word);

		const scores: [number, number] = [paths[0].length - 1, paths[1].length - 1];
		const movesMade = state.movesMade + 1;
		const next: WordLadderState = { ...state, at, paths, scores, movesMade };
		const them = otherPlayer(player);

		/*
		 * The race is really about who climbs in fewer steps, and strict turn
		 * alternation hides that: from the same start, whoever moves first would
		 * always land first under equal play — a guaranteed win for the opening
		 * seat. So landing only wins outright when the other player has already
		 * had as many goes. Landing while they are a ply behind gives them one
		 * final rung to answer with: match it and the ladder was climbed in the
		 * same number of steps, which is a tie.
		 */
		if (move.word === state.target) {
			if (at[them] === state.target) {
				// Their answering step also landed — both climbed in equal steps.
				return { ok: true, state: { ...next, turn: null, result: { winner: null } } };
			}
			if (paths[them].length < paths[player].length) {
				return { ok: true, state: { ...next, turn: them } };
			}
			return { ok: true, state: { ...next, turn: null, result: { winner: player } } };
		}
		if (at[them] === state.target) {
			// The final answering step went somewhere else — the ladder is lost.
			return { ok: true, state: { ...next, turn: null, result: { winner: them } } };
		}
		// Nobody got there. Closest wins, so a stalemate still has an answer.
		if (movesMade >= RUNG_MOVE_LIMIT) {
			return { ok: true, state: settleOnCloseness(next) };
		}
		return { ok: true, state: { ...next, turn: them } };
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		return laddersFrom(state.at[player]).map((word) => ({ word }));
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};

/**
 * The rung that gets closest to the target. Ties are broken by the order the
 * word list happens to be in, which is arbitrary enough to look like a choice.
 */
export function bestLadderStep(state: WordLadderState, player: 0 | 1): WordLadderMove | null {
	const distances = distancesFrom(state.target);
	let best: WordLadderMove | null = null;
	let bestDistance = Number.POSITIVE_INFINITY;

	for (const word of laddersFrom(state.at[player])) {
		const distance = distances.get(word) ?? Number.POSITIVE_INFINITY;
		if (distance < bestDistance) {
			bestDistance = distance;
			best = { word };
		}
	}
	return best;
}
